export const TICK_RATE = 60;
export const TICK_SECONDS = 1 / TICK_RATE;

export interface Vec2 {
  x: number;
  y: number;
}

export interface Arena {
  width: number;
  height: number;
}

export type EnemyKind =
  | "crumb"
  | "mite"
  | "scuttler"
  | "brute"
  | "roller"
  | "stinger"
  | "slime"
  | "moth"
  | "charger"
  | "shellback"
  | "red-captain"
  | "void-cook"
  | "iron-maw"
  | "jam-queen"
  | "clockwork-chef"
  | "the-breakfast"
  | "tree";

export type EnemyRank = "normal" | "elite" | "boss" | "neutral";
export type EnemyBehavior =
  | "chase"
  | "zigzag"
  | "orbit"
  | "charge"
  | "drift"
  | "pulse"
  | "stationary";

export interface EnemyDefinition {
  id: EnemyKind;
  name: string;
  rank: EnemyRank;
  behavior: EnemyBehavior;
  minimumWave: number;
  radius: number;
  health: number;
  speed: number;
  damage: number;
  materialValue: number;
  color: string;
}

export interface Player {
  position: Vec2;
  velocity: Vec2;
  radius: number;
  health: number;
  maxHealth: number;
  speed: number;
  armor: number;
  damageMultiplier: number;
  meleeDamage: number;
  rangedDamage: number;
  elementalDamage: number;
  attackSpeedMultiplier: number;
  critChance: number;
  lifeStealChance: number;
  projectileSpeedMultiplier: number;
  range: number;
  pickupRadius: number;
  dodgeChance: number;
  regenerationPerSecond: number;
  luck: number;
  harvesting: number;
  engineering: number;
  knockback: number;
  regenerationProgress: number;
  bonusProjectiles: number;
  weaponSlotLimit: number;
  invulnerableTicks: number;
  level: number;
  experience: number;
  experienceToNext: number;
  materials: number;
  materialsCollected: number;
  kills: number;
}

export interface Enemy {
  id: number;
  kind: EnemyKind;
  position: Vec2;
  velocity: Vec2;
  radius: number;
  health: number;
  maxHealth: number;
  speed: number;
  contactDamage: number;
  materialValue: number;
  hitFlashTicks: number;
  aiTicks: number;
}

export interface Projectile {
  id: number;
  position: Vec2;
  velocity: Vec2;
  radius: number;
  damage: number;
  color: string;
  shape: ProjectileShape;
  lifetimeTicks: number;
  remainingPierces: number;
  remainingBounces: number;
  explosionRadius: number;
  behavior: WeaponBehavior;
  hitEnemyIds: number[];
}

export type ProjectileShape = "round" | "needle" | "blade" | "orb";

export interface StatModifiers {
  maxHealth?: number;
  speed?: number;
  armor?: number;
  damagePercent?: number;
  meleeDamage?: number;
  rangedDamage?: number;
  elementalDamage?: number;
  attackSpeedPercent?: number;
  critChancePercent?: number;
  lifeStealPercent?: number;
  projectileSpeedPercent?: number;
  range?: number;
  pickupRadius?: number;
  dodgePercent?: number;
  regenerationPerSecond?: number;
  luck?: number;
  harvesting?: number;
  engineering?: number;
  knockback?: number;
}

export type WeaponFamily =
  | "precise"
  | "rapid"
  | "heavy"
  | "elemental"
  | "kitchen"
  | "weird";

export type WeaponBehavior =
  | "projectile"
  | "melee"
  | "explosive"
  | "bouncing"
  | "orbiting"
  | "structure";

export interface WeaponDefinition {
  id: string;
  name: string;
  description: string;
  family: WeaponFamily;
  behavior: WeaponBehavior;
  basePrice: number;
  damage: number;
  intervalTicks: number;
  projectileSpeed: number;
  projectileRadius: number;
  projectileLifetimeTicks: number;
  projectileCount: number;
  spreadRadians: number;
  pierces: number;
  color: string;
  shape: ProjectileShape;
}

export interface WeaponInstance {
  definitionId: string;
  tier: number;
  cooldownTicks: number;
}

export interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  basePrice: number;
  rarity: 1 | 2 | 3 | 4;
  modifiers: StatModifiers;
  effect?: ItemEffect;
  limit?: number;
}

export type ItemEffect =
  | { type: "material-on-kill"; chance: number; amount: number }
  | { type: "heal-on-pickup"; amount: number }
  | { type: "damage-on-pickup"; damage: number }
  | { type: "damage-on-enemy-death"; damage: number }
  | { type: "harvesting-growth"; percent: number }
  | { type: "free-rerolls"; amount: number }
  | { type: "tree-spawns"; amount: number }
  | { type: "weapon-upgrade-on-shop" }
  | { type: "low-health-damage"; percent: number };

export interface UpgradeDefinition {
  id: string;
  name: string;
  description: string;
  modifiers: StatModifiers;
}

export interface UpgradeOffer {
  id: number;
  definitionId: string;
  tier: 1 | 2 | 3 | 4;
}

export interface EggDefinition {
  id: string;
  name: string;
  archetype: string;
  description: string;
  startingWeaponId: string;
  modifiers: StatModifiers;
  bonusProjectiles?: number;
  weaponSlotLimit?: number;
  accent: string;
}

export interface ShopOffer {
  id: number;
  kind: "weapon" | "item";
  definitionId: string;
  tier: 1 | 2 | 3 | 4;
  price: number;
  sold: boolean;
  locked: boolean;
}

export interface Pickup {
  id: number;
  position: Vec2;
  velocity: Vec2;
  radius: number;
  kind: "material" | "consumable" | "crate";
  value: number;
}

export interface LootReward {
  id: number;
  definitionId: string;
  tier: 1 | 2 | 3 | 4;
}

export type BattlePhase =
  | "combat"
  | "paused"
  | "loot"
  | "levelup"
  | "shop"
  | "victory"
  | "gameover";

export interface BattleState {
  seed: number;
  randomState: number;
  tick: number;
  nextEntityId: number;
  arena: Arena;
  phase: BattlePhase;
  wave: number;
  waveTicksRemaining: number;
  waveDurationTicks: number;
  spawnTicks: number;
  treeSpawnTicks: number;
  pendingLevelUps: number;
  upgradeChoices: UpgradeOffer[];
  upgradeRefreshes: number;
  shopOffers: ShopOffer[];
  shopRefreshes: number;
  eggId: string;
  dangerLevel: 0 | 1 | 2 | 3 | 4 | 5;
  endless: boolean;
  hordeWave: boolean;
  baggedMaterials: number;
  lootQueue: LootReward[];
  specialEnemySpawned: boolean;
  weaponClassBonuses: StatModifiers;
  availableWeaponIds: string[];
  availableItemIds: string[];
  weapons: WeaponInstance[];
  items: string[];
  player: Player;
  enemies: Enemy[];
  projectiles: Projectile[];
  pickups: Pickup[];
}

export interface BattleInput {
  moveX: number;
  moveY: number;
}

export type ShellStage = "smooth" | "hairline" | "deep";

export interface ShellBonuses {
  stage: ShellStage;
  armor: number;
  attackSpeedMultiplier: number;
  damageMultiplier: number;
}

export type BattleEvent =
  | { type: "shot"; x: number; y: number }
  | { type: "critical-hit"; x: number; y: number }
  | { type: "enemy-hit"; x: number; y: number; damage: number }
  | { type: "enemy-killed"; x: number; y: number; kind: EnemyKind }
  | { type: "player-hurt"; damage: number }
  | { type: "pickup"; value: number }
  | { type: "level-up"; level: number }
  | { type: "purchase"; name: string }
  | { type: "weapon-merged"; name: string; tier: number }
  | { type: "weapon-recycled"; name: string; value: number }
  | { type: "boss-spawned"; name: string }
  | { type: "countdown"; seconds: number }
  | { type: "wave-cleared"; wave: number }
  | { type: "wave-start"; wave: number }
  | { type: "phase-change"; phase: BattlePhase };

export interface BattleConfig {
  waveSeconds: number;
  enemyHealthMultiplier: number;
  enemySpeedMultiplier: number;
  enemyDamageMultiplier: number;
  dangerLevel?: 0 | 1 | 2 | 3 | 4 | 5;
  startingWeaponId?: string;
  endless?: boolean;
  dynamicWaveDuration?: boolean;
  unlockedWeaponIds?: string[];
  unlockedItemIds?: string[];
}
